import React, { useState, useRef, useEffect } from "react";
import {
  Plus,
  X,
  Check,
  Search,
  ChevronDown,
} from "lucide-react";
import { FiPhone, FiSend } from 'react-icons/fi';
import axios from "axios";
import { apiClient } from '../config/apiConfig';
import 'tailwindcss/tailwind.css';
import 'react-toastify/dist/ReactToastify.css';
import styles from './DealOfTheDay.module.css';


// --------------------
// Type Definitions
// --------------------

interface Variant {
  variantId: number;
  weight: number;
  unit: string;
  price: number;
  stock: number;
}

interface SelectedVariant {
  variantId: number;
  weight: number;
  unit: string;
  price: number;
}
interface Product {
  id: number;
  name: string;
  quantity: string; // shown value (ex: "500 g")
  price: number;
  categoryId: number;
  categoryName: string;
  variants?: Variant[];

  // 👇 NEW (only for selected products)
  selectedVariant?: SelectedVariant;
}
interface Deal {
  id: number;
  title: string;
  description: string;
  products: Product[];
  dealPrice: number;
  originalPrice: number;
  savings: number;
  endTime: string;
}



interface Category {
  categorieId: number;
  name: string;
  imageUrl?: string;
}

// --------------------
// Component
// --------------------

const GroceryDealUI: React.FC = () => {
  const [activeView, setActiveView] = useState<"admin" | "buyer">("admin");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Admin State
  const [dealTitle, setDealTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [dealPrice, setDealPrice] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [dealEndTime, setDealEndTime] = useState<string>("");



  const [deals, setDeals] = useState<Deal[]>([]);

  // Buyer State


  const [searchQuery, setSearchQuery] = useState<string>("");
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | "all">("all");
  const [showProductDropdown, setShowProductDropdown] = useState<boolean>(false);
 
  const addProductToDeal = (product: Product) => {
    if (selectedProducts.find(p => p.id === product.id)) return;

    const firstVariant = product.variants?.[0];

    setSelectedProducts(prev => [
      ...prev,
      {
        ...product,
        selectedVariant: firstVariant
          ? {
            variantId: firstVariant.variantId,
            weight: firstVariant.weight,
            unit: firstVariant.unit,
            price: firstVariant.price,
          }
          : undefined,
        quantity: firstVariant
          ? `${firstVariant.weight} ${firstVariant.unit}`
          : product.quantity,
        price: firstVariant ? firstVariant.price : product.price,
      }
    ]);
  };
  const removeProductFromDeal = (productId: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  };
  const handleVariantChange = (
    productId: number,
    variant: Variant
  ) => {
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === productId
          ? {
            ...p,
            selectedVariant: {
              variantId: variant.variantId,
              weight: variant.weight,
              unit: variant.unit,
              price: variant.price,
            },
            quantity: `${variant.weight} ${variant.unit}`,
            price: variant.price,
          }
          : p
      )
    );
  };
  const createDeal = async () => {
    if (
      !dealTitle ||
      selectedProducts.length === 0 ||
      dealPrice === "" ||
      dealEndTime === ""
    ) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const payload = {
        dealTitle: dealTitle,
        description: description,
        dealPrice: Number(dealPrice),
        startDate: new Date().toISOString(),
        endDate: new Date(dealEndTime).toISOString(),
        productIds: selectedProducts.map(p => p.id) // ✅ VERY IMPORTANT
      };

      const res = await axios.post(
        "https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/DealoftheDay/CreateDeal",
        payload
      );

      alert("Deal created successfully");

      // reset form
      setDealTitle("");
      setDescription("");
      setDealPrice("");
      setDealEndTime("");
      setSelectedProducts([]);

      // reload deals
      fetchDeals();

    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to create deal");
    }
  };

  const filteredProducts = availableProducts.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.quantity.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" ||
      product.categoryId === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    apiClient.get("/Categories/GetCategories")
      .then(res => {
        setCategories([
          { categorieId: 0, name: "All Products" },
          ...res.data
        ]);
      })
      .catch(err => console.error(err));
  }, []);

  // Helper function to get minimum price from variants
  const getMinPrice = (variants?: Variant[]): number => {
    if (!variants || variants.length === 0) return 0;
    return Math.min(...variants.map(v => v.price));
  };

  useEffect(() => {
    apiClient.get("/AddProduct/GetAllProducts?pageNumber=1&pageSize=100")
      .then(res => {
        const mappedProducts: Product[] = res.data.map((p: any) => {
          const variants = p.variants || p.Variants || [];
          return {
            id: p.productId,
            name: p.productName,
            quantity: p.productQuantity || "100g",
            price: getMinPrice(variants),
            categoryId: p.category?.categorieId || p.categorieId || 0,
            categoryName: p.category?.name || p.categoryName || "",
            variants: variants
          };
        });

        setAvailableProducts(mappedProducts);
      })
      .catch(err => console.error("Error fetching products:", err));
  }, []);
  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = () => {
    axios
      .get("https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/DealoftheDay/GetAllDeals")
      .then(res => {
        console.log("API Response:", res.data); // Debug log
        const mappedDeals: Deal[] = res.data.map((d: any) => ({
          id: d.id,
          title: d.title,
          description: d.description,
          dealPrice: d.dealPrice,
          originalPrice: d.originalPrice,
          savings: d.savings,
          endTime: d.endTime,
          products: d.products
        }));
        console.log("Mapped deals:", mappedDeals); // Debug log
        setDeals(mappedDeals);
      })
      .catch(err => {
        console.error("Fetch deals failed", err);
        console.error("Error details:", err.response?.data); // Better error logging
      });
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const isDealExpired = (endTime: string) => {
    return new Date(endTime).getTime() < new Date().getTime();
  };

  // --------------------
  // JSX
  // --------------------

  return (
    <div className={styles.dealContainer}>
      <div className="py-1 bg-primary">
        <div className="container">
          <div className="row no-gutters d-flex align-items-start align-items-center px-md-0">
            <div className="col-lg-12 d-block">
              <div className="row d-flex">
                <div className="col-md pr-4 d-flex topper align-items-center">
                  <div className="icon mr-2 d-flex justify-content-center align-items-center"><FiPhone size={12} color="white" /></div>
                  <span className="text">+91 9148761750</span>
                </div>
                <div className="col-md pr-4 d-flex topper align-items-center">
                  <div className="icon mr-2 d-flex justify-content-center align-items-center"><FiSend size={12} color="white" /></div>
                  <span className="text">care@fhome.shop</span>
                </div>
                <div className="col-md-5 pr-4 d-flex topper align-items-center text-lg-right">
                  <span className="text">3-5 Business days delivery &amp; Free Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <nav className="navbar navbar-expand-lg navbar-dark ftco_navbar bg-dark ftco-navbar-light" id="ftco-navbar">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img
              src="/images/fhome-logo.jpg"
              alt=""
              style={{ height: "55px", marginRight: "8px", borderRadius: "50%", width: "80px" }}
            />
            <span style={{ fontWeight: 'bold', fontSize: '20px', color: '#28a745' }}></span>
          </a>

          <div className="collapse navbar-collapse" id="ftco-nav">
            <ul className="navbar-nav ml-auto">
              <li className="nav-item active"><a href="/admin/categories" className="nav-link">AdminCategories</a></li>
              <li className="nav-item active"><a href="/admin/deal-of-the-day" className="nav-link">DealOfTheDay</a></li>
              <li className="nav-item"><a href="/admin" className="nav-link">Admin Products</a></li>
              <li className="nav-item"><a href="/admin/orders" className="nav-link">Admin Orders</a></li>
            </ul>
          </div>
        </div>
      </nav>
      {/* Admin View */}
      {activeView === 'admin' && (
        <div className={styles.adminSection}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Deal of the Day</h2>

            <div>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Deal Title *
                </label>
                <input
                  type="text"
                  value={dealTitle}
                  onChange={(e) => setDealTitle(e.target.value)}
                  placeholder="e.g., Breakfast Combo"
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the deal"
                  rows={3}
                  className={styles.textarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Add Products to Deal *
                </label>

                {/* Combobox Dropdown Selector */}
                <div className={styles.dropdownWrapper} ref={dropdownRef}>
                  <div className="relative">
                    <Search className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search and select products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setShowProductDropdown(true)}
                      className={styles.searchInput}
                    />
                    <button
                      type="button"
                      onClick={() => setShowProductDropdown(!showProductDropdown)}
                      className={styles.dropdownButton}
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform ${showProductDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {/* Selected Count Badge */}
                  {selectedProducts.length > 0 && (
                    <div className={styles.selectedBadge}>
                      {selectedProducts.length}
                    </div>
                  )}

                  {/* Dropdown Menu */}
                  {showProductDropdown && (
                    <div className={styles.dropdownMenu}>
                      {/* Category Filter */}
                      <div className={styles.categoryFilter}>
                        <div>
                          {categories.map(cat => (
                            <button
                              key={cat.categorieId}
                              type="button"
                              onClick={() =>
                                setSelectedCategory(cat.categorieId === 0 ? "all" : cat.categorieId)
                              }
                              className={`${styles.categoryButton} ${selectedCategory === cat.categorieId ? styles.active : styles.inactive
                                }`}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Product List with Scroll */}
                      <div className={styles.productList}>
                        <div>
                          {filteredProducts.map(product => {
                            const isSelected = selectedProducts.find(p => p.id === product.id);
                            return (
                              <button
                                key={product.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    removeProductFromDeal(product.id);
                                  } else {
                                    addProductToDeal(product);
                                  }
                                }}
                                className={`${styles.productItem} ${isSelected ? styles.selected : ''}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`${styles.checkboxContainer} ${isSelected ? styles.checked : ''}`}>
                                    {isSelected && <Check className={`${styles.checkmark}`} />}
                                  </div>
                                  <div className={styles.productDetails}>
                                    <div className={styles.productName}>{product.name}</div>
                                    {product.variants && product.variants.length > 0 ? (
                                      <select
                                        className={styles.variantDropdown}
                                        value={
                                          selectedProducts.find(p => p.id === product.id)?.selectedVariant?.variantId
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const variant = product.variants?.find(
                                            v => v.variantId === Number(e.target.value)
                                          );
                                          if (variant) {
                                            handleVariantChange(product.id, variant);
                                          }
                                        }}
                                      >
                                        {product.variants.map(variant => (
                                          <option key={variant.variantId} value={variant.variantId}>
                                            {variant.weight} {variant.unit} – ₹{variant.price}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <div className={styles.productQuantity}>{product.quantity}</div>
                                    )}

                                  </div>
                                </div>
                                <span className={styles.productPrice}>
                                  ₹{
                                    selectedProducts.find(p => p.id === product.id)?.price
                                    ?? product.price
                                  }
                                </span>
                              </button>
                            );
                          })}
                          {filteredProducts.length === 0 && (
                            <div className={styles.noResults}>
                              No products found. Try a different search or category.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Dropdown Footer */}
                      {selectedProducts.length > 0 && (
                        <div className={styles.dropdownFooter}>
                          <div>
                            <span className="text-gray-700 font-medium">
                              {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
                            </span>
                            <span className="text-green-700 font-bold">
                              Total: ₹{selectedProducts.reduce((sum, p) => sum + p.price, 0)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Products Display */}
                {selectedProducts.length > 0 && (
                  <div className={styles.selectedProducts}>
                    <h3 className={styles.selectedProductsTitle}>Selected Products:</h3>
                    <div>
                      {selectedProducts.map(product => {
                        const selectedProduct = selectedProducts.find(p => p.id === product.id);
                        return (
                        <div key={product.id} className={styles.selectedProductItem}>
                          <span className="text-gray-700">
                            {product.name} ({product.quantity})
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-600">₹{product.price}</span>
                            <button
                              type="button"
                              onClick={() => removeProductFromDeal(product.id)}
                              className={styles.removeButton}
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        );
                      })}
                      <div className={styles.totalRow}>
                        <span>Original Total:</span>
                        <span>₹{selectedProducts.reduce((sum, p) => sum + p.price, 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Deal Price * (₹)
                </label>
                <input
                  type="number"
                  value={dealPrice}
                  onChange={(e) => setDealPrice(e.target.value)}
                  placeholder="Enter special deal price"
                  className={styles.input}
                />
                {dealPrice && selectedProducts.length > 0 && (
                  <p className={styles.savingsMessage}>
                    Savings: ₹{selectedProducts.reduce((sum, p) => sum + p.price, 0) - parseFloat(dealPrice)}
                  </p>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Deal End Time *
                </label>
                <input
                  type="datetime-local"
                  value={dealEndTime}
                  onChange={(e) => setDealEndTime(e.target.value)}
                  className={styles.input}
                />
              </div>
              <button
                type="button"
                onClick={createDeal}
                disabled={!dealTitle || selectedProducts.length === 0 || dealPrice === ""}
                className={styles.createButton}
              >
                Create Deal
              </button>

            </div>
          </div>

          {/* Existing Deals */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Active Deals</h2>
            <div className={styles.dealsSection}>
              {deals && deals.length > 0 ? (
                deals.map(deal => (
                  <div key={deal.id} className={styles.dealCard}>
                    <div className={styles.dealHeader}>
                      <div>
                        <h3 className={styles.dealTitle}>{deal.title}</h3>
                        <p className={styles.dealDescription}>{deal.description}</p>
                      </div>
                      <span className={styles.savingsBadge}>
                        Save ₹{deal.savings}
                      </span>
                    </div>
                    <div className={styles.productsList}>
                      {deal.products && deal.products.length > 0 ? (
                        deal.products.map(product => (
                          <div key={product.id} className={styles.productBullet}>
                            • {product.name} ({product.quantity})
                          </div>
                        ))
                      ) : (
                        <div className={styles.productBullet}>No products in this deal</div>
                      )}
                    </div>
                    <div className={styles.dealFooter}>
                      <div>
                        <span className={styles.originalPrice}>₹{deal.originalPrice}</span>
                        <span className={styles.dealPrice}>₹{deal.dealPrice}</span>
                      </div>
                      <span className={`${isDealExpired(deal.endTime)
                        ? "text-red-600 font-bold"
                        : "text-green-600"
                        }`}>
                        {isDealExpired(deal.endTime)
                          ? "Deal Expired"
                          : `Ends at: ${new Date(deal.endTime).toLocaleString()}`}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                  No deals available at the moment.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroceryDealUI;

